import React, { useCallback, useEffect, useState } from 'react';
import Card from '@material-ui/core/Card';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { get } from 'lodash';
import createLoadingSelector from '../../../selectors/loading';
import { CREATE_RX_FORM_PREFIX, createRxFormAction } from '../../../reducers/rxForms';
import OrganizationInfo from '../../../components/OrganizationInfo';
import LoadAvailableLocations from '../../../components/LoadAvailableLocations';
import LoadingIndicator from '../../../components/LoadingIndicator';
import useOrganizationPermissions from '../../../hooks/useOrganizationPermissions';
import OrganizationsApiService from '../../../services/api/organizations';
import Form from '../../../components/RxForm/Form';
import createIdbNativeFields from '../../../components/RxForm/IDB/nativeFields';
import { createIdbValidationSchema } from '../../../components/RxForm/IDB/validation';
import { getCustomFieldOverride } from '../../../components/RxForm/IDB/customFieldOverrides';
import {
  getDefaultAssignment,
  getDefaultTrayDesignAssignment
} from '../../../components/RxForm/IDB/helpers';
import LoadingButton from '../../../components/LoadingButton';
import createGetItemSelector from '../../../selectors/getItem';
import { enqueueNotification } from '../../../reducers/notifications';
import {
  IDB_ATTRIBUTES,
  ROOT_INTEGRATION_TYPES,
  SCAN_SUBMISSION_METHODS
} from '../../../components/RxForm/IDB/constants';
import hasPermission from '../../../selectors/hasPermission';
import withOrganizationPricing from '../../../hooks/withOrganizationPricing';
import { fetchFormBuilderAction } from '../../../reducers/formBuilder';
import {
  CONTROL_CHECKBOXES,
  CONTROL_DROPDOWN,
  CONTROL_EXISTING,
  CONTROL_FILE,
  CONTROL_RADIOS
} from '../../Account/components/RxFormCustomization/FormBuilder/helpers';
import SavingProcess from '../SavingProcess';
import { clearCustomBracketSetsAction } from '../../../reducers/customBracketSets';

const useStyles = makeStyles(() => ({
  form: {
    width: '70%',
    marginLeft: 'auto',
    marginRight: 'auto',
    '& .MuiCardHeader-action': {
      flex: '0.5 0 auto'
    }
  }
}));

const AddIdbRxForm = ({ match: { params } }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const organizationId = parseInt(params.organizationId, 10);
  const isManagement = useSelector(state => state.auth.isManagement);
  const { isLoading: isLoadingOrganizationPermissions } = useOrganizationPermissions({
    organizationId
  });
  const organizationFromState = useSelector(state =>
    createGetItemSelector(state.organizations, organizationId)
  );
  const currentUserOrganization = useSelector(state => state.auth.currentOrganization);
  const currentUser = useSelector(state => state.auth.user);

  const [preference, setPreference] = useState();
  const [hiddenFields, setHiddenFields] = useState([]);
  const [thirdPartyPartnerName, setThirdPartyPartnerName] = useState();
  const canSelectDoctor = useSelector(state =>
    hasPermission(state, {
      permissions: [
        'rx-forms.manage',
        'rx-forms.select-doctor',
        'organization.rx-forms.select-doctor'
      ],
      organizationId
    })
  );

  const isCreatingRxForm = useSelector(state =>
    createLoadingSelector([CREATE_RX_FORM_PREFIX])(state)
  );
  const [organization, setOrganization] = useState(null);

  const [fieldsToInclude, setFieldsToInclude] = useState([]);
  const [formBuilderFields, setFormBuilderFields] = useState({});
  const formBuilderForm = useSelector(state => state.formBuilder.form);
  const formStructure = useSelector(state => state.formBuilder.layout);
  const newControls = Object.values(get(formBuilderForm, 'controls', {})).filter(
    control => control.system_type !== CONTROL_EXISTING
  );

  const [saving, setSaving] = useState(null);

  useEffect(() => {
    const builderParams = {
      organization_id: organizationId,
      type: 'idb'
    };
    dispatch(fetchFormBuilderAction(builderParams));
  }, [organizationId, dispatch]);

  const navigate = () => {
    if (isManagement) {
      history.push('/patients-page');
    } else {
      history.push('/patients');
    }
  };

  useEffect(() => {
    const organizationApiService = new OrganizationsApiService();
    organizationApiService.getRxFormPreference(organizationId).then(({ data, metadata }) => {
      setPreference(data);
      setThirdPartyPartnerName(metadata.third_party_partner.name);
      setHiddenFields(metadata.hidden_fields);
    });
  }, [organizationId]);

  useEffect(() => {
    if (organization && organization.id === organizationId) {
      return () => {};
    }
    if (isManagement && organizationFromState) {
      setOrganization(organizationFromState);
      return () => {};
    }
    if (currentUserOrganization) {
      setOrganization(currentUserOrganization);
      return () => {};
    }

    let setFetchOrganization = data => {
      setOrganization(data);
    };
    const api = new OrganizationsApiService();
    api
      .show(organizationId, ['locations'])
      .then(data => {
        setFetchOrganization(data.organization);
      })
      .catch(() => {
        setOrganization(null);
        dispatch(enqueueNotification('error', 'Failed to fetch organization.'));
      });

    return () => {
      setFetchOrganization = () => {};
    };
  }, [
    organization,
    setOrganization,
    organizationId,
    isManagement,
    organizationFromState,
    currentUserOrganization,
    dispatch
  ]);

  useEffect(() => {
    const MULTIPLE_OPTION_TYPES = [CONTROL_DROPDOWN, CONTROL_CHECKBOXES, CONTROL_RADIOS];

    newControls.forEach(control => {
      if (formBuilderFields[control.system_name] === undefined) {
        let value = control.system_type === CONTROL_FILE ? [] : '';

        const defaultValueString = get(control, 'props.default', null);
        if (defaultValueString !== null) {
          if (MULTIPLE_OPTION_TYPES.includes(control.system_type)) {
            const option = get(control, 'props.options', []).find(
              opt => opt.name.toLowerCase() === defaultValueString.toLowerCase()
            );

            if (option) value = option.id;
          } else {
            value = defaultValueString;
          }
        }

        setFormBuilderFields(prevState => {
          return {
            ...prevState,
            [control.system_name]: value
          };
        });
      }
    });
  }, [formBuilderFields, formBuilderForm, newControls]);

  useEffect(() => {
    setFormBuilderFields(prevState => {
      return {
        ...prevState,
        form_builder_layout_id: get(formBuilderForm, 'form_builder_layout_id', '')
      };
    });
  }, [formBuilderForm]);

  useEffect(() => {
    const allControls = Object.values(get(formBuilderForm, 'controls', {}));
    setFieldsToInclude(() => {
      const controls = allControls.map(control => control.system_name);
      return [...controls];
    });
  }, [setFieldsToInclude, formBuilderForm]);

  const cleanValues = useCallback(
    values => {
      const controls = {};
      const newVals = { ...values };
      Object.values(get(formBuilderForm, 'controls', {})).forEach(con => {
        controls[con.system_name] = con;
      });

      if (!controls.tray_design_assignment) {
        newVals.tray_design_assignment = '';
      }
      return newVals;
    },
    [formBuilderForm]
  );

  const createRxForm = useCallback(
    additionalValues => {
      const saveValues = {
        ...cleanValues(saving),
        ...additionalValues
      };
      return dispatch(createRxFormAction(saveValues));
    },
    [saving, dispatch, cleanValues]
  );

  const handleSubmit = (values, { setSubmitting }) => {
    setSaving({
      ...values,
      organization_id: organization.id,
      type: 'idb'
    });
    setSubmitting(false);
  };

  if (!organization || !preference || isLoadingOrganizationPermissions) {
    return <LoadingIndicator />;
  }

  const rxForm = {
    first_name: '',
    last_name: '',
    case_number_text: '',
    intraoral_scan_date: moment().format('YYYY-MM-DD'),
    submission_completed_by: getDefaultAssignment(preference.initial_case_setup_assignment_options),
    rush_case: preference.rush_case,
    rush_case_authorized_by: '',
    scan_submission_type: SCAN_SUBMISSION_METHODS.UPLOAD,
    scanner_type: '1',
    custom_scanner_type_name: '',
    stl_files: [],
    rx_intraoral_photos: [],
    special_instructions: '',
    jaws_id: preference.arch_type,
    export_type: preference.export_type,
    print_assignment: getDefaultAssignment(preference.print_assignment_options),
    tray_design_assignment: getDefaultTrayDesignAssignment(preference),
    insert_brackets_assignment: preference.insert_brackets_assignment,
    location_id: preference.treatment_location_id,
    shipping_location_id: preference.shipping_location_id,
    shipping_method: null, // let the ShippingMethodField take care setting the correct method
    [IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE]: ROOT_INTEGRATION_TYPES.NONE,
    [IDB_ATTRIBUTES.BRACKET_LIBRARIES]: [],
    [IDB_ATTRIBUTES.CUSTOM_BRACKET_SET_ID]: null,
    bonding_date: null,
    [IDB_ATTRIBUTES.DOCTOR]: canSelectDoctor ? preference.default_doctor_id : null,
    [IDB_ATTRIBUTES.DICOM_FILES]: [],
    [IDB_ATTRIBUTES.ARCH_WIRES]: [],
    teeth: {
      u00: 0,
      u01: 0,
      u02: 0,
      u03: 0,
      u04: 0,
      u05: 0,
      u06: 0,
      u07: 0,
      u08: 0,
      u09: 0,
      u10: 0,
      u11: 0,
      u12: 0,
      u13: 0,
      u14: 0,
      u15: 0,
      l00: 0,
      l01: 0,
      l02: 0,
      l03: 0,
      l04: 0,
      l05: 0,
      l06: 0,
      l07: 0,
      l08: 0,
      l09: 0,
      l10: 0,
      l11: 0,
      l12: 0,
      l13: 0,
      l14: 0,
      l15: 0
    },
    [IDB_ATTRIBUTES.CUSTOM_SETUP]: 0,
    [IDB_ATTRIBUTES.SAVED_FOR_LATER]: false,
    ...formBuilderFields
  };

  if (canSelectDoctor) {
    rxForm[IDB_ATTRIBUTES.DOCTOR] = preference.default_doctor_id;
  } else if (currentUser) {
    rxForm[IDB_ATTRIBUTES.DOCTOR] = currentUser.id;
  }

  const children = pricing => (
    <Card className={classes.form}>
      <LoadAvailableLocations organization={organization}>
        {availableLocations => (
          <Form
            organization={organization}
            title="Start A Case"
            description={<OrganizationInfo organization={organization} />}
            rxForm={rxForm}
            formStructure={formStructure}
            newControls={newControls}
            only={fieldsToInclude}
            nativeFields={createIdbNativeFields}
            createValidationSchema={createIdbValidationSchema}
            getCustomFieldOverride={getCustomFieldOverride}
            onSubmit={handleSubmit}
            thirdPartyPartnerName={thirdPartyPartnerName}
            preference={preference}
            isLoading={isCreatingRxForm}
            availableLocations={availableLocations}
            hiddenFields={hiddenFields}
            actions={({ dirty, isValid, submitForm, status }) => {
              return (
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={!!saving}
                  disabled={!dirty || !isValid || isCreatingRxForm || !!status || !!saving}
                  onClick={submitForm}
                >
                  Submit
                </LoadingButton>
              );
            }}
            pricing={pricing}
            saveForLaterEnabled
          />
        )}
      </LoadAvailableLocations>
      {!!saving && (
        <SavingProcess
          skipCustomBracketSetCreationRequest={!organization.enable_custom_bracket_sets}
          values={saving}
          submit={createRxForm}
          cancel={() => setSaving(null)}
          finish={() => navigate()}
          resetCustomBracketSets={() => dispatch(clearCustomBracketSetsAction(organization.id))}
        />
      )}
    </Card>
  );

  return withOrganizationPricing({ organizationId: organization.id, children });
};

AddIdbRxForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  match: PropTypes.object
};

AddIdbRxForm.defaultProps = {
  match: { params: {} }
};

// export default AddIdbRxForm;

export default AddIdbRxForm;
